import React, { useState, useEffect } from "react";
import { Modal, ProgressBar, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
    FaUser,
    FaPhone,
    FaLock,
    FaCheck,
    FaSchool,
    FaBroadcastTower,
    FaPalette,
    FaGlobe,
    FaEnvelope,
    FaInfoCircle,
    FaPlus,
    FaStream,
    FaClock,
    FaChevronLeft,
    FaChevronRight,
    FaTimes,
    FaImage,
    FaUsersCog
} from "react-icons/fa";
import CropperModalAdmin from "./CropperModalAdmin";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import CountryCodeDropDownAdmin from "../CountryDropDownAdmin/CountryDropDownAdmin";
import CustomInput from "../TextField/TextFeild";
import parsePhoneNumberFromString from "libphonenumber-js";
import SelectBox from "../CountryDropDownAdmin/SelectBox";
import { HUMAN_TIMEZONES, STATION_GROUPS, STATION_GROUPS_1 } from "../../constants";
import { registerStationAdmin, sendNewInviteWithoutEmail, DeleteInviteRequest } from "../../store/slices/invitesSlice";
import { getUserRoleInfo } from "../../helpers/roleUtils";
import { resellStations } from "../../helpers/utils";

const getBasePersonalSchema = (isResellerUser) => ({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email format").required("Email is required"),
    phone: Yup.string()
        .required("Phone number is required")
        .test("isValidPhone", "Invalid phone number", (value) => {
            if (!value) return false;
            const formattedValue = value.startsWith("+") ? value : `+${value}`;
            const parsedNumber = parsePhoneNumberFromString(formattedValue);
            return parsedNumber ? parsedNumber.isValid() : false;
        }),
    school: Yup.string().required("Organization is required"),
    stationGroup: isResellerUser
        ? Yup.string()
        : Yup.string().required("Station Group is Required."),
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters"),
    passwordRepeat: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    agree: Yup.boolean().oneOf([true], "You must agree to terms"),
});

const getValidationSchema = (isResellerUser, isGroupAdminOnly) => {
    const base = getBasePersonalSchema(isResellerUser);
    if (isGroupAdminOnly) {
        return Yup.object(base);
    }
    return Yup.object({
        ...base,
        stationSchool: Yup.string().required("School/Organization is required"),
        stationName: Yup.string().required("Station name is required"),
        stationDescription: Yup.string().trim().required("Station description is required"),
        stationTheme: Yup.string().required("Theme color is required"),
        stationWebsite: Yup.string().url("Invalid URL format"),
        stationEmail: Yup.string().email("Invalid email format").required("Station Email is required"),
        stationPhone: Yup.string()
            .required("Phone number is required")
            .test("isValidPhone", "Invalid phone number", (value) => {
                if (!value) return false;
                const formattedValue = value.startsWith("+") ? value : `+${value}`;
                const parsedNumber = parsePhoneNumberFromString(formattedValue);
                return parsedNumber ? parsedNumber.isValid() : false;
            }),
        stationInfoPhone: Yup.string()
            .required("Phone number is required")
            .test("isValidPhone", "Invalid phone number", (value) => {
                if (!value) return false;
                const formattedValue = value.startsWith("+") ? value : `+${value}`;
                const parsedNumber = parsePhoneNumberFromString(formattedValue);
                return parsedNumber ? parsedNumber.isValid() : false;
            }),
        stream: Yup.string().trim().required("Stream URL is required").url("Invalid URL format"),
        stationLogo: Yup.string().required("Station logo is required"),
        stationTimezone: Yup.string().required("Timezone is required"),
    });
};

const initialValues = {
    firstName: "",
    lastName: "",
    email: '',
    school: '',
    phone: "+1",
    password: "",
    stationLogo: "",
    stationGroup: '',
    passwordRepeat: "",
    agree: true,
    stationSchool: "",
    stationName: "",
    stationDescription: "",
    stationTheme: "#3a3e52",
    stationWebsite: "",
    stationEmail: "",
    stationPhone: "+1",
    stationInfoPhone: "+1",
    stream: "",
    stationTimezone: "US/Arizona",
};

const stationGroupOptions = Object.entries(STATION_GROUPS_1).map(([key, label]) => ({
    label,
    value: key,
}));

const CreateStationAdmin = ({ show, onClose }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const roleInfo = getUserRoleInfo(user);
    const isReseller = roleInfo.isReseller;
    const resellerStationGroup = user?.attributes?.roles && resellStations(user.attributes.roles)[0] || null;

    const [step, setStep] = useState(1);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [isGroupAdminOnly, setIsGroupAdminOnly] = useState(false);

    const validate = React.useCallback(
        (values) => {
            const schema = getValidationSchema(isReseller, isGroupAdminOnly);
            return schema.validate(values, { abortEarly: false }).then(() => ({})).catch((err) => {
                const errors = {};
                err.inner?.forEach((e) => {
                    if (e.path) errors[e.path] = e.message;
                });
                return errors;
            });
        },
        [isReseller, isGroupAdminOnly]
    );

    const formik = useFormik({
        initialValues,
        validate,
        onSubmit: async (values) => {
            try {
                const stationGroup = isReseller ? (resellerStationGroup || values.stationGroup) : values.stationGroup;
                if (!stationGroup) {
                    toast.error("Station group could not be determined.");
                    return;
                }
                const invitePayload = {
                    type: 'invite',
                    attributes: {
                        email: values.email,
                        stationGroup,
                        ctx: {
                            sgv: stationGroup === 'radiomax' ? 1 : 0,
                            school: values.school,
                            firstName: values.firstName,
                            lastName: values.lastName,
                        }
                    }
                };

                const inviteToken = await dispatch(
                    sendNewInviteWithoutEmail({
                        payload: invitePayload
                    })
                ).unwrap();

                const isStationAdmin = isGroupAdminOnly;
                let adminPayload;
                let successMessage;

                if (isStationAdmin) {
                    adminPayload = {
                        data: {
                            id: values.email,
                            type: "user",
                            attributes: {
                                firstName: values.firstName,
                                lastName: values.lastName,
                                phone: values.phone,
                                password: values.password,
                                inviteToken,
                                isStationAdmin: true,
                            },
                        },
                    };
                    successMessage = "Group admin created successfully!";
                } else {
                    const { email, school, stationGroup: _sg, stationTimezone, ...restValues } = values;
                    const { passwordRepeat, agree, ...filteredValues } = restValues;
                    adminPayload = {
                        data: {
                            id: values.email,
                            type: "user",
                            attributes: {
                                ...filteredValues,
                                inviteToken: inviteToken,
                                stationTimezone: HUMAN_TIMEZONES[stationTimezone]
                            },
                        },
                    };
                    successMessage = "Station admin created successfully!";
                }

                try {
                    await dispatch(
                        registerStationAdmin({
                            message: successMessage,
                            payload: adminPayload
                        })
                    ).unwrap();
                    toast.success(successMessage);
                    onClose();
                } catch (registerError) {
                    // Invite was created but registration failed — remove invite so form can be reused
                    try {
                        await dispatch(
                            DeleteInviteRequest({
                                id: values.email,
                                silent: true,
                            })
                        ).unwrap();
                    } catch {
                        // Still surface the registration error below
                    }
                    throw registerError;
                }
            } catch (error) {
                const errorMessage =
                    typeof error === "string"
                        ? error
                        : error?.payload || error?.message || "Error creating station admin";
                toast.error(errorMessage);
            }
        },
    });

    useEffect(() => {
        if (show) {
            const initial = { ...initialValues };
            if (isReseller && resellerStationGroup) {
                initial.stationGroup = resellerStationGroup;
            }
            formik.resetForm({ values: initial });
            setStep(1);
            setShowColorPicker(false);
            setCroppedImage(null);
            setIsGroupAdminOnly(false);
        }
    }, [show, isReseller, resellerStationGroup]);

    const nextStep = async () => {
        const step1Fields = ["firstName", "lastName", "phone", "password", "passwordRepeat", "agree", "school", "email"];
        if (!isReseller) {
            step1Fields.push("stationGroup");
        }
        const stepFields = {
            1: step1Fields,
            2: [
                "stationSchool",
                "stationName",
                "stationDescription",
                "stationTheme",
                "stationEmail",
                "stationPhone",
                "stationInfoPhone",
                "stream",
                "stationTimezone",
                "stationLogo",
            ],
        };

        const fieldsToValidate = stepFields[step];
        if (!fieldsToValidate) return;

        fieldsToValidate.forEach((field) => {
            formik.setFieldTouched(field, true, false);
        });

        const errors = await formik.validateForm();
        const hasStepErrors = fieldsToValidate.some((field) => errors[field]);

        if (hasStepErrors) return;

        if (step < 3) {
            setTimeout(() => setStep((currentStep) => currentStep + 1), 0);
        }
    };

    const prevStep = () => {
        setStep(step - 1);
    };

    const handleColorChange = (color) => {
        formik.setFieldValue("stationTheme", color.hex);
    };

    const handleCrop = (croppedImg) => {
        setCroppedImage(croppedImg);
        formik.setFieldValue("stationLogo", croppedImg);
        setShowModal(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setShowModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <>
            <Modal
                show={show}
                size="lg"
                onHide={onClose}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: isGroupAdminOnly ? "#3a3e52" : formik.values.stationTheme,
                        color: "#fff",
                        borderBottom: `3px solid ${isGroupAdminOnly ? "#3a3e52" : formik.values.stationTheme}`
                    }}
                >
                    <Modal.Title className="d-flex align-items-center">
                        {step === 1 && <FaUser className="me-2" />}
                        {step === 2 && !isGroupAdminOnly && <FaBroadcastTower className="me-2" />}
                        {step === 3 && !isGroupAdminOnly && <FaCheck className="me-2" />}
                        {step === 1 && "Personal Information"}
                        {step === 2 && !isGroupAdminOnly && "Station Details"}
                        {step === 3 && !isGroupAdminOnly && "Review & Submit"}
                    </Modal.Title>
                </Modal.Header>

                <ProgressBar
                    now={isGroupAdminOnly ? 100 : (step / 3) * 100}
                    variant="success"
                    style={{ height: "5px" }}
                    className="mb-3"
                />

                <form onSubmit={formik.handleSubmit}>
                    <Modal.Body>
                        {step === 1 && (
                            <>
                                <div className="mb-4">
                                    <Form.Check
                                        type="switch"
                                        id="create-group-admin-toggle"
                                        label={
                                            <span className="d-flex align-items-center">
                                                <FaUsersCog className="me-2 text-primary" />
                                                <strong>Create group admin only</strong>
                                            </span>
                                        }
                                        checked={isGroupAdminOnly}
                                        onChange={(e) => setIsGroupAdminOnly(e.target.checked)}
                                    />
                                </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaUser />}
                                        label="First Name"
                                        id="firstName"
                                        name="firstName"
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.firstName && formik.errors.firstName}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaUser />}
                                        label="Last Name"
                                        id="lastName"
                                        name="lastName"
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.lastName && formik.errors.lastName}
                                        required
                                    />
                                </div>

                                <div className="col-md-12">
                                    <div className="custom-form-group d-flex justify-space-between">
                                        <CountryCodeDropDownAdmin
                                            label={"Phone"}
                                            phoneNumber={formik.values.phone}
                                            onPhoneNumberChange={(value) => {
                                                formik.setFieldValue("phone", value);
                                            }}
                                        />
                                        <hr
                                            className={
                                                formik.touched.phone && formik.errors.phone
                                                    ? "error-hr-line"
                                                    : ""
                                            }
                                        />
                                        {formik.touched.phone && formik.errors.phone && (
                                            <div className="error-text">
                                                {formik.errors.phone}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <CustomInput
                                        label="Email"
                                        id="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && formik.errors.email}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        label="Organization"
                                        id="school"
                                        name="school"
                                        value={formik.values.school}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.school && formik.errors.school}
                                        required
                                    />
                                </div>
                                {!isReseller ? (
                                    <div className="col-md-6">
                                        <SelectBox
                                            name="stationGroup"
                                            selectedValue={formik.values.stationGroup}
                                            onChangeFn={(value) => {
                                                formik.setFieldValue("stationGroup", value);
                                            }}
                                            label="Group"
                                            options={stationGroupOptions}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.stationGroup}
                                            error={formik.touched.stationGroup && formik.errors.stationGroup}
                                            isSearchable={false}
                                            multiple={false}
                                        />
                                    </div>
                                ) : resellerStationGroup ? (
                                    <div className="col-md-6">
                                        <div className="custom-form-group d-flex flex-column">
                                        <small className="text-muted">Your station group (reseller)</small>
                                            <div className="form-control border bg-light" style={{ padding: "0.375rem 0.75rem" }}>
                                                {STATION_GROUPS_1[resellerStationGroup] || resellerStationGroup}
                                            </div>
                                            
                                        </div>
                                    </div>
                                ) : null}
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaLock />}
                                        label="Password"
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.password && formik.errors.password}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaLock />}
                                        label="Repeat Password"
                                        id="passwordRepeat"
                                        name="passwordRepeat"
                                        type="password"
                                        value={formik.values.passwordRepeat}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.passwordRepeat && formik.errors.passwordRepeat}
                                        required
                                    />
                                </div>
                            </div>
                            </>
                        )}

                        {!isGroupAdminOnly && step === 2 && (
                            <div className="row">
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaSchool />}
                                        label="Station Name/School Name"
                                        id="stationSchool"
                                        name="stationSchool"
                                        value={formik.values.stationSchool}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationSchool && formik.errors.stationSchool}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaBroadcastTower />}
                                        label="Call Letters/City & State"
                                        id="stationName"
                                        name="stationName"
                                        value={formik.values.stationName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationName && formik.errors.stationName}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaStream />}
                                        label="Station Description"
                                        id="stationDescription"
                                        name="stationDescription"
                                        isTextArea={true}
                                        value={formik.values.stationDescription}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationDescription && formik.errors.stationDescription}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3 position-relative custom-form-group">
                                        <label className="label">
                                            <FaPalette className="me-2" />
                                            Station Theme Color
                                        </label>
                                        <div className="d-flex align-items-center pt-5">
                                            <div
                                                style={{
                                                    width: "30px",
                                                    height: "30px",
                                                    backgroundColor: formik.values.stationTheme,
                                                    borderRadius: "4px",
                                                    marginRight: "10px",
                                                    cursor: "pointer",
                                                    border: "1px solid #ced4da",
                                                }}
                                                onClick={() => setShowColorPicker(!showColorPicker)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control border p-2 w-100 mt-0"
                                                style={{ width: "250px" }}
                                                value={formik.values.stationTheme}
                                                readOnly
                                            />
                                        </div>
                                        {showColorPicker && (
                                            <div className="mt-2">
                                                <ChromePicker
                                                    color={formik.values.stationTheme}
                                                    onChange={handleColorChange}
                                                />
                                            </div>
                                        )}
                                        {formik.touched.stationTheme && formik.errors.stationTheme && (
                                            <div className="text-danger small mt-1">{formik.errors.stationTheme}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaGlobe />}
                                        label="Station Website"
                                        id="stationWebsite"
                                        name="stationWebsite"
                                        value={formik.values.stationWebsite}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationWebsite && formik.errors.stationWebsite}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaEnvelope />}
                                        label="Station Email"
                                        id="stationEmail"
                                        name="stationEmail"
                                        type="email"
                                        value={formik.values.stationEmail}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationEmail && formik.errors.stationEmail}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="custom-form-group d-flex justify-space-between">
                                        <CountryCodeDropDownAdmin
                                            label={"Phone Icon #"}
                                            phoneNumber={formik.values.stationPhone}
                                            onPhoneNumberChange={(value) => {
                                                formik.setFieldValue("stationPhone", value);
                                            }}
                                        />
                                        <hr
                                            className={
                                                formik.touched.stationPhone && formik.errors.stationPhone
                                                    ? "error-hr-line"
                                                    : ""
                                            }
                                        />
                                        {formik.touched.stationPhone && formik.errors.stationPhone && (
                                            <div className="error-text">
                                                {formik.errors.stationPhone}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="custom-form-group d-flex justify-space-between">
                                        <CountryCodeDropDownAdmin
                                            label={"Station Info Phone"}
                                            phoneNumber={formik.values.stationInfoPhone}
                                            onPhoneNumberChange={(value) => {
                                                formik.setFieldValue("stationInfoPhone", value);
                                            }}
                                        />
                                        <hr
                                            className={
                                                formik.touched.stationInfoPhone && formik.errors.stationInfoPhone
                                                    ? "error-hr-line"
                                                    : ""
                                            }
                                        />
                                        {formik.touched.stationInfoPhone && formik.errors.stationInfoPhone && (
                                            <div className="error-text">
                                                {formik.errors.stationInfoPhone}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <CustomInput
                                        icon={<FaStream />}
                                        label="Stream URL"
                                        id="stream"
                                        name="stream"
                                        value={formik.values.stream}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stream && formik.errors.stream}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <TimezoneSelect
                                        label="Timezone"
                                        id="stationTimezone"
                                        name="stationTimezone"
                                        value={formik.values.stationTimezone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.stationTimezone && formik.errors.stationTimezone}
                                        required
                                    />
                                </div>
                                <div className="col-md-12">
                                    <div className="d-flex flex-column align-items-center m-4">
                                        <div className="position-relative mb-3">
                                            {croppedImage ? (
                                                <img
                                                    src={croppedImage}
                                                    alt="Station Logo"
                                                    className="img-fluid"
                                                    style={{
                                                        width: "200px",
                                                        height: "120px",
                                                        objectFit: "contain",
                                                        border: "2px solid #ddd",
                                                        borderRadius: "8px",
                                                        backgroundColor: "#f8f9fa"
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="d-flex flex-column align-items-center justify-content-center"
                                                    style={{
                                                        width: "150px",
                                                        height: "150px",
                                                        backgroundColor: "#f8f9fa",
                                                        border: "2px dashed #ddd",
                                                        borderRadius: "8px"
                                                    }}
                                                >
                                                    <FaImage size={40} color="#aaa" className="mb-2" />
                                                    <small className="text-muted">Station Logo</small>
                                                </div>
                                            )}
                                            <label
                                                htmlFor="profileUpload"
                                                className="btn btn-sm btn-primary position-absolute"
                                                style={{
                                                    bottom: "-15px",
                                                    right: "10px",
                                                    borderRadius: "50%",
                                                    width: "30px",
                                                    height: "30px",
                                                    padding: "0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                <FaPlus size={12} />
                                            </label>
                                            <input
                                                type="file"
                                                id="profileUpload"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                style={{ display: "none" }}
                                            />
                                        </div>
                                        <small className="text-muted">Click to upload station logo</small>
                                        {formik.touched.stationLogo && formik.errors.stationLogo && (
                                            <div className="text-danger small mt-1">{formik.errors.stationLogo}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isGroupAdminOnly && step === 3 && (
                            <div className="review-section">
                                <h5 className="mb-4 d-flex align-items-center">
                                    <FaCheck className="me-2 text-success" />
                                    Please review your information
                                </h5>

                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="card mb-4">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 d-flex align-items-center">
                                                    <FaUser className="me-2" />
                                                    Personal Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-2">
                                                        <strong>First Name:</strong>
                                                        <div>{formik.values.firstName}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Last Name:</strong>
                                                        <div>{formik.values.lastName}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Phone:</strong>
                                                        <div>{formik.values.phone}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Email:</strong>
                                                        <div>{formik.values.email}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Organization:</strong>
                                                        <div>{formik.values.school}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Group:</strong>
                                                        <div>{STATION_GROUPS_1[formik.values.stationGroup] || formik.values.stationGroup}</div>
                                                    </div>
                                                    {croppedImage && (
                                                        <div className="col-md-12 text-center">
                                                            <img
                                                                src={croppedImage}
                                                                alt="Profile"
                                                                className="rounded-circle"
                                                                style={{
                                                                    width: "100px",
                                                                    height: "100px",
                                                                    objectFit: "cover",
                                                                    border: "2px solid #ddd"
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <div className="card">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0 d-flex align-items-center">
                                                    <FaBroadcastTower className="me-2" />
                                                    Station Details
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Station Name/School Name:</strong>
                                                        <div>{formik.values.stationSchool}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Call Letters/City & State:</strong>
                                                        <div>{formik.values.stationName}</div>
                                                    </div>
                                                    <div className="col-md-12 mb-2">
                                                        <strong>Description:</strong>
                                                        <div>{formik.values.stationDescription || "N/A"}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Theme Color:</strong>
                                                        <div className="d-flex align-items-center">
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    width: "15px",
                                                                    height: "15px",
                                                                    backgroundColor: formik.values.stationTheme,
                                                                    marginRight: "5px",
                                                                    border: "1px solid #ddd"
                                                                }}
                                                            />
                                                            {formik.values.stationTheme}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Website:</strong>
                                                        <div>{formik.values.stationWebsite || "N/A"}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Station Email:</strong>
                                                        <div>{formik.values.stationEmail || "N/A"}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Phone Icon #:</strong>
                                                        <div>{formik.values.stationPhone}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Info Phone:</strong>
                                                        <div>{formik.values.stationInfoPhone || "N/A"}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Stream URL:</strong>
                                                        <div>{formik.values.stream || "N/A"}</div>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Timezone:</strong>
                                                        <div>{formik.values.stationTimezone}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <div className="d-flex justify-content-between w-100">
                            <div>
                                {!isGroupAdminOnly && step > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={prevStep}
                                    >
                                        <FaChevronLeft className="me-1" />
                                        Previous
                                    </button>
                                )}
                            </div>

                            <div>
                                {isGroupAdminOnly ? (
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheck className="me-1" />
                                                Create Group Admin
                                            </>
                                        )}
                                    </button>
                                ) : step < 3 ? (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={nextStep}
                                    >
                                        Next
                                        <FaChevronRight className="ms-1" />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheck className="me-1" />
                                                Submit
                                            </>
                                        )}
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-2"
                                    onClick={onClose}
                                >
                                    <FaTimes className="me-1" />
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>
            <CropperModalAdmin
                show={showModal}
                onClose={() => setShowModal(false)}
                image={image}
                onCrop={handleCrop}
            />
        </>
    );
};

export default CreateStationAdmin;